import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { TICKETS } from "@/constants/testIds/tickets";
import { timeAgo, timeUntil, initials, formatDuration } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, PaperPlaneTilt, Sparkle, LockKey, Clock, User as UserIcon, ChatCircle, Cpu, PencilSimple, EnvelopeSimple, ArrowBendUpLeft, ArrowUUpLeft, CaretDown, CaretUp, Paperclip, X as XIcon, LinkSimple as LinkIcon, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import MentionTextarea from "@/components/MentionTextarea";
import { useSettings } from "@/lib/settings";
import { useAuth } from "@/lib/auth";

import { ClassificationCard } from "./ticket/ClassificationCard";
import { GeographyCard } from "./ticket/GeographyCard";
import { DuplicatesCard } from "./ticket/DuplicatesCard";
import { TimewritingCard } from "./ticket/TimewritingCard";
import { AttachmentList } from "./ticket/AttachmentList";
import { MessageCard } from "./ticket/MessageCard";
import { KeyDateWidget, ChecklistSection, InlineEditText, HistorySection } from "./ticket/TicketExtras";
import { CcCard } from "./ticket/CcCard";

const STATUSES = ["new", "waiting", "in_progress", "resolved", "withdrawn"];
const PRIORITIES = ["low", "medium", "high", "urgent"];


export default function TicketDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { settings, reload: reloadSettings } = useSettings();
  const slaEnabled = settings?.sla_enabled !== false;
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [users, setUsers] = useState([]);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [sending, setSending] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // File[] to upload with next reply
  const [mentionIds, setMentionIds] = useState([]); // user ids @-mentioned in current draft
  const emailReplyDefault = settings?.email_reply_default !== false;

  useEffect(() => { setSendEmail(emailReplyDefault); }, [emailReplyDefault]);

  // Pre-fill the user's signature into the compose box the first time
  // the user opens the ticket (or lands on it fresh). We only touch
  // the box when it's empty so we never overwrite an in-flight draft.
  // The signature value lives on the user profile; falls back to
  // "Thanks, {name}" when unset. Users can edit before sending.
  useEffect(() => {
    if (!user) return;
    if (user.email_signature_enabled === false) return;
    if (reply) return;
    const sig = (user.email_signature != null)
      ? user.email_signature
      : `Thanks,\n${user.name || ""}`;
    if (sig && sig.trim()) {
      setReply(`\n\n${sig}`);
    }
    // Only run on initial ticket load — tying to id so switching
    // tickets re-primes the box with a fresh signature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  const load = () => {
    api.get(`/tickets/${id}`).then((r) => setTicket(r.data));
    api.get(`/tickets/${id}/comments`).then((r) => setComments(r.data));
    api.get(`/tickets/${id}/activity`).then((r) => setActivity(r.data));
    api.get(`/tickets/${id}/attachments`).then((r) => setAttachments(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)); }, [id]);

  const [refetching, setRefetching] = useState(false);
  const refetchEmailAttachments = async () => {
    setRefetching(true);
    try {
      const r = await api.post(`/tickets/${id}/refetch-email-attachments`);
      const { added, scanned, recovered_ghosts = 0 } = r.data;
      const bits = [];
      if (added > 0) bits.push(`added ${added}`);
      if (recovered_ghosts > 0) bits.push(`replaced ${recovered_ghosts} missing`);
      if (bits.length === 0) {
        toast.info(`No new attachments — checked ${scanned} email${scanned === 1 ? "" : "s"}`);
      } else {
        toast.success(`${bits.join(" · ")} · scanned ${scanned} email${scanned === 1 ? "" : "s"}`);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-fetch failed");
    } finally {
      setRefetching(false);
    }
  };

  // group attachments: ticket-level (no comment_id) and per-comment
  const ticketAttachments = attachments.filter((a) => !a.comment_id);
  const attsByComment = attachments.reduce((m, a) => {
    if (a.comment_id) (m[a.comment_id] = m[a.comment_id] || []).push(a);
    return m;
  }, {});

  const sendReply = async () => {
    if (!reply.trim() && pendingFiles.length === 0) return;
    setSending(true);
    try {
      // 1) Upload files first (unlinked), collect their IDs
      const uploadedIds = [];
      for (const f of pendingFiles) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/tickets/${id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploadedIds.push(r.data.id);
      }
      // 2) Create the comment; server will re-parent the attachments and include them in the outbound email
      await api.post(`/tickets/${id}/comments`, {
        body: reply || (pendingFiles.length ? `(${pendingFiles.length} attachment${pendingFiles.length > 1 ? "s" : ""})` : ""),
        internal,
        send_email: internal ? false : sendEmail,
        attachment_ids: uploadedIds,
        mention_user_ids: mentionIds,
      });
      setReply("");
      setPendingFiles([]);
      setMentionIds([]);
      load();
      toast.success(internal ? "Internal note added" : (sendEmail ? "Reply sent (emailed)" : "Reply saved"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  const patch = async (updates) => {
    // Stage 4 — withdrawing requires a reason. Prompt the user for one
    // before firing the PATCH so the backend never rejects with 400.
    if (updates.status === "withdrawn" && ticket.status !== "withdrawn") {
      // eslint-disable-next-line no-alert
      const reason = window.prompt(
        "Why are you withdrawing this ticket?\n\nExamples: 'Candidate declined offer', 'Req pulled', 'Duplicate of #1234'.",
      );
      if (!reason || !reason.trim()) {
        toast.info("Withdraw cancelled");
        return;
      }
      updates.withdrawn_reason = reason.trim();
    }
    try {
      const r = await api.patch(`/tickets/${id}`, updates);
      setTicket(r.data);
      api.get(`/tickets/${id}/activity`).then((res) => setActivity(res.data));
      toast.success("Updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const aiSuggest = async () => {
    setAiLoading(true);
    try {
      const r = await api.post(`/tickets/${id}/ai-suggest`);
      if (r.data.suggested_reply) {
        setReply(r.data.suggested_reply);
        toast.success("AI reply drafted");
      } else { toast.error("No suggestion available"); }
    } catch { toast.error("AI suggest failed"); }
    finally { setAiLoading(false); }
  };

  if (!ticket) return <div className="text-slate-500 text-sm">Loading…</div>;

  const sla = timeUntil(ticket.sla_resolution_due);
  const assignee = users.find((u) => u.id === ticket.assignee_id);
  // Smart back — prefer an explicit `location.state.from` set by the
  // referring page (e.g. New Starts calendar), otherwise walk the
  // React Router history one step. Falls back to /tickets when the
  // ticket was opened via direct URL / new tab so the button never
  // dead-ends the user.
  const fromPath = location.state?.from;
  const fromLabel = location.state?.fromLabel;
  const goBack = () => {
    if (fromPath) {
      navigate(fromPath);
    } else if ((window.history.state?.idx ?? 0) > 0) {
      navigate(-1);
    } else {
      navigate("/tickets");
    }
  };

  return (
    <div className="space-y-4" data-testid={TICKETS.ticketDetail}>
      <div>
        <button
          type="button"
          onClick={goBack}
          className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 font-mono w-fit"
          data-testid="ticket-back-button"
        >
          <ArrowLeft size={12} /> {fromLabel ? `Back to ${fromLabel}` : "Back"}
        </button>
        <div className="flex items-baseline gap-3 mt-2 flex-wrap">
          <span className="font-mono text-sm text-slate-500">#{ticket.number}</span>
          <div className="flex-1 min-w-[200px]">
            <InlineEditText
              value={ticket.subject}
              testId="ticket-subject-edit"
              className="font-heading text-2xl font-bold tracking-tight"
              onSave={async (v) => {
                if (!v.trim() || v === ticket.subject) return;
                await api.patch(`/tickets/${id}`, { subject: v.trim() });
                load();
              }}
            />
          </div>
        </div>
        {(ticket.subject_name || ticket.awaiting_details) && (
          <div className="mt-1 flex items-center gap-2 text-sm" data-testid="ticket-subject-name">
            <span className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">About:</span>
            <InlineEditText
              value={ticket.subject_name || ""}
              placeholder={ticket.awaiting_details ? "e.g. New starter's name (awaiting)" : "Add subject person…"}
              testId="ticket-subject-name-edit"
              className="text-sm font-medium text-[#4f4c90]"
              onSave={async (v) => {
                const next = (v || "").trim();
                if (next === (ticket.subject_name || "")) return;
                await api.patch(`/tickets/${id}`, { subject_name: next || null });
                load();
              }}
            />
          </div>
        )}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {ticket.awaiting_details && (
            <span className="chip !bg-amber-50 !text-amber-800 !border-amber-200" data-testid="ticket-awaiting-details-badge">
              Awaiting details ({(ticket.pending_field_ids || []).length})
            </span>
          )}
          {ticket.status === "withdrawn" && (
            <span className="chip !bg-red-50 !text-red-800 !border-red-200" data-testid="ticket-withdrawn-badge">
              Withdrawn{ticket.withdrawn_reason ? ` — ${ticket.withdrawn_reason}` : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
          <span>Opened by {ticket.requester_name} · {ticket.requester_email}</span>
          <span>·</span><span className="font-mono">{timeAgo(ticket.created_at)}</span>
          <span>·</span><span className="capitalize">via {ticket.source}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-4 min-w-0">
          {ticket.approval_status && (
            <div
              className={`hairline rounded-lg p-3 flex items-start gap-3 ${
                ticket.approval_status === "pending" ? "bg-amber-50 border-amber-200"
                : ticket.approval_status === "approved" ? "bg-emerald-50 border-emerald-200"
                : "bg-red-50 border-red-200"
              }`}
              data-testid="ticket-approval-banner"
            >
              <div className={`w-1 self-stretch rounded-full ${
                ticket.approval_status === "pending" ? "bg-amber-400"
                : ticket.approval_status === "approved" ? "bg-emerald-500"
                : "bg-red-500"
              }`} />
              <div className="flex-1">
                <div className={`text-[11px] uppercase font-medium tracking-wider ${
                  ticket.approval_status === "pending" ? "text-amber-800"
                  : ticket.approval_status === "approved" ? "text-emerald-800"
                  : "text-red-800"
                }`}>
                  {ticket.approval_status === "pending" && "⏸ Awaiting manager approval"}
                  {ticket.approval_status === "approved" && "✓ Approved by manager"}
                  {ticket.approval_status === "rejected" && "✗ Rejected by manager"}
                </div>
                <div className="text-xs text-slate-700 mt-1">
                  {ticket.approval_status === "pending" && (
                    <>Approval email sent {ticket.approval_requested_at ? timeAgo(ticket.approval_requested_at) : ""} — the ticket will move to New once the manager approves it.</>
                  )}
                  {ticket.approval_status === "approved" && (
                    <>
                      Approved by <b>{ticket.approval_decided_by_email}</b> {ticket.approval_decided_at ? timeAgo(ticket.approval_decided_at) : ""}.
                      {ticket.approval_manager_note ? <> Comment: <i>{ticket.approval_manager_note}</i></> : null}
                    </>
                  )}
                  {ticket.approval_status === "rejected" && (
                    <>
                      Rejected by <b>{ticket.approval_decided_by_email}</b> {ticket.approval_decided_at ? timeAgo(ticket.approval_decided_at) : ""}.
                      {ticket.approval_manager_note ? <> Comment: <i>{ticket.approval_manager_note}</i></> : null}
                    </>
                  )}
                </div>
                {ticket.approval_status === "pending" && ticket.approval_manager_email && (
                  <ResendApprovalButton
                    ticketId={id}
                    managerEmail={ticket.approval_manager_email}
                    onSent={load}
                  />
                )}
              </div>
            </div>
          )}

          <KeyDateWidget ticket={ticket} onSaved={load} />

          <ChecklistSection ticket={ticket} onChanged={load} />

          {ticket.ai_summary && (
            <div className="rounded-lg p-3 bg-[#F5F4FA] border border-[#4f4c90]/25" data-testid="ai-summary-card">
              <div className="text-[11px] text-[#4f4c90] flex items-center gap-1 font-medium tracking-wider uppercase"><Sparkle size={12} weight="fill" /> AI Summary</div>
              <div className="text-sm text-slate-800 mt-1 leading-relaxed">{ticket.ai_summary}</div>
            </div>
          )}

          <MessageCard
            variant={ticket.email_meta ? "original" : "reply"}
            authorName={ticket.requester_name}
            timestamp={ticket.created_at}
            subtitleExtra={
              ticket.email_meta
                ? `${ticket.email_meta.from_email} → support · ${ticket.email_meta.subject || "no subject"}`
                : "original request"
            }
            body={ticket.description}
            attachments={ticketAttachments}
            testid="ticket-description"
            onEdit={async (newBody) => {
              if (!newBody.trim() || newBody === ticket.description) return;
              await api.patch(`/tickets/${id}`, { description: newBody });
              load();
            }}
          />

          {/* Jun 2026 — composer sits directly below the ticket body
              so the typing area is always visible; the thread beneath
              renders newest-first underneath. */}
          <div className="hairline rounded-lg p-3 bg-white" data-testid="reply-composer">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Switch data-testid={TICKETS.internalToggle} checked={internal} onCheckedChange={setInternal} />
                  <Label className="text-xs text-slate-700">Internal note</Label>
                </div>
                {!internal && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      data-testid="ticket-send-email-toggle"
                      checked={sendEmail}
                      onCheckedChange={setSendEmail}
                    />
                    <span className="text-xs text-slate-700">Email requester</span>
                  </label>
                )}
              </div>
              <Button size="sm" variant="ghost" onClick={aiSuggest} disabled={aiLoading}
                data-testid={TICKETS.aiSuggestButton}
                className="text-[#8B87C8] hover:bg-[#4f4c90]/10 h-7 gap-1">
                <Sparkle size={12} weight="fill" /> {aiLoading ? "Thinking…" : "AI draft"}
              </Button>
            </div>
            <MentionTextarea
              testId={TICKETS.replyBox}
              value={reply}
              onChange={setReply}
              users={users.filter((u) => u.role === "admin" || u.role === "agent")}
              onMentionAdd={(uid) => setMentionIds((cur) => cur.includes(uid) ? cur : [...cur, uid])}
              onKeyDown={(e) => {
                if (
                  (e.metaKey || e.ctrlKey) &&
                  e.key === "Enter" &&
                  user?.email_shortcut_send === true &&
                  !internal &&
                  !sending &&
                  (reply.trim() || pendingFiles.length > 0)
                ) {
                  e.preventDefault();
                  sendReply();
                }
              }}
              placeholder={internal ? "Write an internal note (type @ to tag a teammate)…" : (sendEmail ? "Reply to requester (will be emailed) — type @ to tag a teammate…" : "Reply to requester (saved but not emailed)…")}
              className="bg-white border-slate-200 min-h-24 text-sm"
            />
            {!internal && sendEmail && (ticket.cc_emails?.length ?? 0) > 0 && (
              <div className="mt-2 text-[11px] text-slate-500 font-mono" data-testid="reply-cc-hint">
                Will also CC: <span className="text-slate-700">{ticket.cc_emails.join(", ")}</span>
              </div>
            )}
            {mentionIds.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                <span className="text-[11px] text-slate-500 font-mono">Will notify:</span>
                {mentionIds.map((uid) => {
                  const u = users.find((x) => x.id === uid);
                  if (!u) return null;
                  return (
                    <span key={uid} className="chip !text-[#39A36E] !border-[#39A36E]/40 bg-[#39A36E]/10 gap-1">
                      @{u.name}
                      <button
                        onClick={() => setMentionIds((cur) => cur.filter((x) => x !== uid))}
                        className="hover:text-red-500"
                        data-testid={`mention-clear-${u.email}`}
                      >×</button>
                    </span>
                  );
                })}
              </div>
            )}
            {pendingFiles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {pendingFiles.map((f, i) => (
                  <span key={i} className="chip !text-slate-800 !border-slate-300 bg-slate-100 gap-1 pl-2 pr-1">
                    <Paperclip size={10} weight="bold" className="text-[#8B87C8]" />
                    {f.name}
                    <span className="text-[10px] text-slate-500 font-mono">
                      {f.size < 1024 * 1024 ? `${(f.size/1024).toFixed(0)}KB` : `${(f.size/1024/1024).toFixed(1)}MB`}
                    </span>
                    <button
                      onClick={() => setPendingFiles((p) => p.filter((_, j) => j !== i))}
                      className="hover:text-red-400 p-0.5"
                      data-testid={`remove-pending-${i}`}
                    >
                      <XIcon size={10} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between mt-2">
              <label className="cursor-pointer">
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const ok = files.filter((f) => f.size <= 25 * 1024 * 1024);
                    if (ok.length !== files.length) toast.error("Some files exceed 25MB and were skipped");
                    setPendingFiles((p) => [...p, ...ok]);
                    e.target.value = "";
                  }}
                  data-testid="reply-file-input"
                />
                <span className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 px-2 py-1 rounded border border-slate-200 bg-slate-100 hover:bg-slate-200 transition-colors">
                  <Paperclip size={12} weight="bold" /> Attach files
                </span>
              </label>
              <Button data-testid={TICKETS.replySubmit} onClick={sendReply}
                disabled={sending || (!reply.trim() && pendingFiles.length === 0)}
                className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-8 gap-1">
                <PaperPlaneTilt size={14} /> {sending ? "Sending…" : (internal ? "Save note" : (sendEmail ? "Send email" : "Save reply"))}
              </Button>
            </div>
          </div>

          {comments.slice().reverse().map((c) => {
            let variant = "reply";
            if (c.internal) variant = "internal";
            else if (c.channel === "email_in") variant = "email_in";
            else if (c.channel === "email_out") variant = "email_out";
            else if (c.channel === "portal") variant = "portal";
            const subtitleExtra =
              c.channel === "email_in" ? `from ${c.email_meta?.from_email || c.author_name}` :
              c.channel === "email_out" ? `sent to ${c.email_meta?.to || ticket.requester_email}` :
              c.channel === "portal" ? "via ticket link" :
              null;
            return (
              <MessageCard
                key={c.id}
                variant={variant}
                authorName={c.author_name}
                timestamp={c.created_at}
                subtitleExtra={subtitleExtra}
                body={c.body}
                attachments={attsByComment[c.id]}
                testid={`comment-${c.id}`}
              />
            );
          })}

          {activity.length > 0 && (
            <div className="hairline rounded-lg p-4 bg-white">
              <div className="text-[11px] uppercase text-slate-500 tracking-wider mb-3 font-medium">Activity</div>
              <div className="space-y-2">
                {activity.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-xs text-slate-500">
                    <ChatCircle size={12} className="text-slate-400" />
                    <span>{a.text}</span>
                    <span className="ml-auto font-mono text-slate-500">{timeAgo(a.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <HistorySection ticket={ticket} users={users} />
        </div>

        <aside className="space-y-3">
          <div className="hairline rounded-lg p-3 bg-white space-y-3">
            <div>
              <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Status</Label>
              <Select value={ticket.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger data-testid={TICKETS.statusDropdown} className="mt-1 bg-[#FFFFFF] border-slate-200 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_"," ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Priority</Label>
              <Select value={ticket.priority} onValueChange={(v) => patch({ priority: v })}>
                <SelectTrigger data-testid={TICKETS.priorityDropdown} className="mt-1 bg-[#FFFFFF] border-slate-200 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              {(() => {
                const teamsArr = Array.isArray(ticket.teams) ? ticket.teams : [];
                const isDual = teamsArr.length > 1;
                const abt = ticket.assignees_by_team || {};
                const assignableUsers = users.filter((u) => u.role !== "requester");
                /**
                 * Users eligible for a given team's slot. A user with no
                 * explicit `teams` tag (empty list) acts as "any" and
                 * shows up in every slot — this preserves the pre-tag
                 * UX for workspaces that haven't started tagging yet.
                 * A user tagged with the slot's team is included; a
                 * user tagged with the OTHER team only is excluded.
                 */
                const forTeam = (team) => assignableUsers.filter((u) => {
                  const t = Array.isArray(u.teams) ? u.teams : [];
                  return t.length === 0 || t.includes(team);
                });
                const setSlot = (team, uid) => {
                  // Build the new per-team dict and PATCH it. The server
                  // mirrors the first non-null value into `assignee_id`
                  // so legacy views still work.
                  const next = { ...abt };
                  next[team] = uid || null;
                  patch({ assignees_by_team: next });
                };
                if (!isDual) {
                  // Single-queue ticket — identical behaviour to before
                  // the dual-assignee change. Hitting Assignee here
                  // writes `assignee_id` AND the single team's slot via
                  // the server-side sync in PATCH /tickets/{id}.
                  return (
                    <>
                      <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Assignee</Label>
                      <Select
                        value={ticket.assignee_id || "none"}
                        onValueChange={(v) => patch({ assignee_id: v === "none" ? null : v })}
                      >
                        <SelectTrigger data-testid={TICKETS.assigneeDropdown} className="mt-1 bg-[#FFFFFF] border-slate-200 h-9"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                          <SelectItem value="none">Unassigned</SelectItem>
                          {assignableUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </>
                  );
                }
                // Both-queue ticket — one slot per team. Labels follow
                // the ticket's `teams` order so admins can predict which
                // one is "primary" (= mirrored into `assignee_id`).
                const label = (t) => (t === "it" ? "IT lead" : "Systems lead");
                return (
                  <div className="space-y-2">
                    {teamsArr.map((team) => (
                      <div key={team}>
                        <Label className="text-[11px] uppercase text-slate-500 tracking-wider">{label(team)}</Label>
                        <Select
                          value={abt[team] || "none"}
                          onValueChange={(v) => setSlot(team, v === "none" ? null : v)}
                        >
                          <SelectTrigger
                            data-testid={`assignee-${team}`}
                            className="mt-1 bg-[#FFFFFF] border-slate-200 h-9"
                          >
                            <SelectValue placeholder="Unassigned" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200">
                            <SelectItem value="none">Unassigned</SelectItem>
                            {forTeam(team).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            <TeamsCard ticket={ticket} onChange={(teams) => setTicket({ ...ticket, teams })} />
          </div>

          {/* Key classification — surfaced above SLA/requester so the most
              important routing info (region, system) is visible at a glance
              without needing to scroll the right-hand column. */}
          <GeographyCard ticket={ticket} onChange={patch} regions={settings?.regions || []} onTaxonomyUpdated={reloadSettings} />

          {(ticket.system || ticket.issue_type) && (
            <ClassificationCard ticket={ticket} onChange={patch} systemsList={settings?.systems} issueTypesList={settings?.issue_types} onTaxonomyUpdated={reloadSettings} />
          )}
          {!ticket.system && !ticket.issue_type && (
            <ClassificationCard ticket={ticket} onChange={patch} empty systemsList={settings?.systems} issueTypesList={settings?.issue_types} onTaxonomyUpdated={reloadSettings} />
          )}

          <div className="hairline rounded-lg p-3 bg-white space-y-2" data-testid="sla-card">
            {ticket.onboarding_deadline || (["Onboarding", "Offboarding"].includes(ticket.issue_type)) ? (
              <OnboardingTracker ticket={ticket} />
            ) : (
              <>
                <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium">SLA</div>
                <div className="flex items-center gap-2 text-xs">
                  <Clock size={14} className="text-slate-500" />
                  {ticket.status === "resolved" ? (
                    <span className="text-slate-600">Ticket resolved</span>
                  ) : ticket.status === "waiting" ? (
                    <span className="font-mono text-[#4f4c90]" data-testid="sla-paused">Paused — waiting on requester</span>
                  ) : (
                    <span className={`font-mono ${sla.breached ? "text-red-500" : "text-slate-700"}`}>{sla.text}</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 font-mono">
                  Due {ticket.sla_resolution_due ? new Date(ticket.sla_resolution_due).toLocaleString() : "—"}
                </div>
                {(ticket.waiting_seconds_total || 0) > 60 && (
                  <div className="text-[11px] text-slate-500 font-mono">
                    Time paused: {formatDuration(ticket.waiting_seconds_total)}
                  </div>
                )}
              </>
            )}
          </div>

          {ticket.issue_type === "Onboarding" && (
            <WelcomeEmailCard ticket={ticket} onChange={patch} onReload={load} />
          )}

          <div className="hairline rounded-lg p-3 bg-white space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium">Requester</div>
              <PortalLinkControls ticket={ticket} onChange={load} />
            </div>
            <div className="flex items-start gap-2 text-xs">
              <UserIcon size={14} className="text-slate-500 mt-0.5" />
              <div className="flex-1 min-w-0">
                <InlineEditText
                  value={ticket.requester_name}
                  placeholder="(no name)"
                  testId="ticket-requester-name-edit"
                  className="text-slate-800 text-xs"
                  onSave={async (v) => {
                    const n = (v || "").trim();
                    if (!n || n === ticket.requester_name) return;
                    try { await api.patch(`/tickets/${id}`, { requester_name: n }); load(); toast.success("Requester updated"); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
                  }}
                />
                <div className="mt-0.5">
                  <InlineEditText
                    value={ticket.requester_email}
                    placeholder="(no email)"
                    testId="ticket-requester-email-edit"
                    className="text-slate-500 font-mono text-[11px]"
                    onSave={async (v) => {
                      const n = (v || "").trim();
                      if (!n || n === ticket.requester_email) return;
                      try { await api.patch(`/tickets/${id}`, { requester_email: n }); load(); toast.success("Requester email updated"); }
                      catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <CcCard ticket={ticket} onChange={setTicket} />

          <DuplicatesCard ticketId={ticket.id} ticketNumber={ticket.number} onMerged={load} />

          <MergeHistoryCard ticket={ticket} onReload={load} />

          <TimewritingCard ticketId={ticket.id} currentUserId={user?.id} isAdmin={isAdmin} />

          {isAdmin && ticket.source === "email" && (
            <button
              type="button"
              onClick={refetchEmailAttachments}
              disabled={refetching}
              data-testid="refetch-email-attachments-btn"
              className="w-full text-[11px] text-slate-500 hover:text-[#4f4c90] hover:bg-slate-50 border border-slate-200 rounded-md py-1.5 flex items-center justify-center gap-1.5 disabled:opacity-60"
              title="Re-scan Microsoft 365 for any inline images or files that were missed on ingest"
            >
              <Paperclip size={11} />
              {refetching ? "Re-fetching…" : "Re-fetch email attachments"}
            </button>
          )}

          <div className="hairline rounded-lg p-3 bg-white text-[11px] font-mono text-slate-500 space-y-1">
            <div>id: {ticket.id.slice(0, 8)}…</div>
            <div>source: {ticket.source}</div>
            <div>created: {new Date(ticket.created_at).toLocaleString()}</div>
            {assignee && <div>assignee: {assignee.name}</div>}
          </div>
        </aside>
      </div>
    </div>
  );
}

/**
 * ResendApprovalButton — small inline control shown in the Approval
 * Status card when approval is still pending. Any authenticated agent
 * or admin can click it. On confirm, the backend voids the existing
 * pending token and dispatches a fresh approval email in a background
 * task, so this call returns immediately (~200ms) and we poll the
 * ticket for the activity-log entry.
 */
function ResendApprovalButton({ ticketId, managerEmail, onSent }) {
  const [sending, setSending] = useState(false);
  const click = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Re-send the approval request to ${managerEmail}?\n\nAny previous approve/reject links will stop working — the manager will get a fresh one.`)) return;
    setSending(true);
    try {
      const r = await api.post(`/tickets/${ticketId}/approval/resend`);
      toast.success(r.data?.detail || `Approval re-sent to ${managerEmail}`);
      if (onSent) onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setSending(false);
    }
  };
  return (
    <button
      type="button"
      onClick={click}
      disabled={sending}
      className="mt-2 text-[11px] font-mono uppercase tracking-wider text-amber-800 hover:text-amber-900 bg-white hover:bg-amber-50 border border-amber-300 rounded px-2 py-1 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
      data-testid="resend-approval-btn"
    >
      {sending ? "Sending…" : `↻ Resend to ${managerEmail}`}
    </button>
  );
}



/**
 * WelcomeEmailCard — Day-1 welcome email panel on Onboarding tickets.
 * Shows send status + derived recipient and lets admins override the
 * recipient / line manager addresses and force a resend. Hidden on
 * non-Onboarding tickets by the parent conditional.
 */
function WelcomeEmailCard({ ticket, onChange, onReload }) {
  const [override, setOverride] = useState(ticket.welcome_email_override || "");
  const [mgrOverride, setMgrOverride] = useState(ticket.welcome_email_manager_override || "");
  const [resending, setResending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const sent = ticket.welcome_email_sent_at;
  const sentTo = ticket.welcome_email_sent_to;
  const blocked = ticket.welcome_email_blocked_reason;

  // Persist override changes on blur.
  const saveOverride = async (field, value) => {
    const payload = {};
    payload[field] = value || null;
    try {
      await onChange(payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save override");
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post(`/tickets/${ticket.id}/welcome-email/resend`);
      toast.success("Welcome email sent");
      setConfirmOpen(false);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="hairline rounded-lg p-3 bg-white space-y-2" data-testid="welcome-email-card">
      <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium">
        Welcome email
      </div>
      {blocked && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Held: {blocked === "duplicate_name"
            ? "another starter already got this derived address — set the override below, then resend."
            : blocked === "placeholder_name"
            ? "the submission's name is a placeholder (e.g. \"Potential Hire\") — set the real address override below, then resend."
            : "the starter's name is missing on the submission — set the override below, then resend."}
        </div>
      )}
      <div className="text-[11px] text-slate-500 font-mono">
        {sent
          ? <>Sent {new Date(sent).toLocaleString()} to <span className="text-slate-700">{sentTo}</span></>
          : "Not sent yet — the daily sweep will send on the start date"}
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Welcome email address override</Label>
        <Input
          data-testid="welcome-override"
          value={override}
          onChange={(e) => setOverride(e.target.value)}
          onBlur={() => saveOverride("welcome_email_override", override.trim())}
          placeholder="firstname.lastname@auroraenergy.co (optional)"
          className="bg-white border-slate-200"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Line manager Cc override</Label>
        <Input
          data-testid="welcome-manager-override"
          value={mgrOverride}
          onChange={(e) => setMgrOverride(e.target.value)}
          onBlur={() => saveOverride("welcome_email_manager_override", mgrOverride.trim())}
          placeholder="manager@auroraenergy.co (optional)"
          className="bg-white border-slate-200"
        />
      </div>
      {isAdmin && (
        <div className="pt-1">
          <Button
            data-testid="welcome-resend"
            variant="outline" size="sm"
            onClick={() => setConfirmOpen(true)}
            className="h-8 border-slate-200"
          >
            {sent ? "Resend welcome email" : "Send welcome email now"}
          </Button>
        </div>
      )}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="welcome-resend-dialog">
          <DialogHeader>
            <DialogTitle>{sent ? "Resend welcome email?" : "Send welcome email now?"}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-1">
            <div>This will send the welcome email to the derived address (or the override, if set) and Cc the line manager.</div>
            {sent && <div className="text-amber-700 text-[11px]">This starter has already received the welcome email on {new Date(sent).toLocaleString()}.</div>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="welcome-resend-cancel">Cancel</Button>
            <Button
              onClick={resend} disabled={resending}
              data-testid="welcome-resend-confirm"
              className="bg-[#4f4c90] text-white hover:bg-[#3d3a73]"
            >
              {resending ? "Sending…" : (sent ? "Resend" : "Send")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



/**
 * OnboardingTracker — replaces the SLA card for Onboarding & Offboarding
 * tickets. Shows the deadline (start-date minus lead-time), how long is
 * left, and once resolved a green/red stamp for on-time vs late.
 */
function OnboardingTracker({ ticket }) {
  const deadline = ticket.onboarding_deadline;
  const key = ticket.key_date;
  const resolved = ticket.resolved_at;
  const status = ticket.onboarding_status;

  const parseDate = (iso) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
    return (y && m && d) ? new Date(y, m - 1, d) : null;
  };
  const fmt = (iso) => {
    const d = parseDate(iso);
    return d ? d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "—";
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const deadlineD = parseDate(deadline);
  const daysLeft = deadlineD ? Math.round((deadlineD - today) / 86400000) : null;

  let label = "Awaiting start date";
  let tone = "text-slate-600";
  if (resolved) {
    if (status === "on_time") { label = "Resolved on time"; tone = "text-[#39A36E]"; }
    else if (status === "late") { label = "Resolved late"; tone = "text-red-500"; }
    else { label = "Resolved"; tone = "text-slate-600"; }
  } else if (deadlineD) {
    if (daysLeft < 0) { label = `Overdue by ${-daysLeft} day${-daysLeft === 1 ? "" : "s"}`; tone = "text-red-500"; }
    else if (daysLeft === 0) { label = "Due today"; tone = "text-amber-600"; }
    else if (daysLeft <= 2) { label = `On track — ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`; tone = "text-amber-600"; }
    else { label = `On track — ${daysLeft} days left`; tone = "text-[#39A36E]"; }
  }

  return (
    <div data-testid="onboarding-tracker">
      <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium">
        {ticket.issue_type} tracker
      </div>
      <div className={`flex items-center gap-2 text-xs mt-1 ${tone}`}>
        <span className="font-mono" data-testid="onboarding-tracker-label">{label}</span>
      </div>
      <div className="text-[11px] text-slate-500 font-mono mt-1">
        Start date: {fmt(key)}
      </div>
      <div className="text-[11px] text-slate-500 font-mono">
        Deadline: {fmt(deadline)}
      </div>
      {!deadline && !resolved && (
        <div className="text-[11px] text-amber-700 italic mt-1">
          Fill in the start date to activate the tracker.
        </div>
      )}
    </div>
  );
}


/**
 * PortalLinkControls — compact menu next to the Requester heading that
 * lets an agent copy, regenerate, or revoke the tokenized portal link.
 * Shows expiry so it's obvious when the link is about to stop working.
 */
function PortalLinkControls({ ticket, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const active = !!ticket.public_token;
  const expiresAt = ticket.public_token_expires_at
    ? new Date(ticket.public_token_expires_at)
    : null;
  const now = new Date();
  const daysLeft = expiresAt ? Math.ceil((expiresAt - now) / 86400000) : null;
  const isExpired = expiresAt && expiresAt < now;

  const copy = async () => {
    const url = `${window.location.origin}/portal/t/${ticket.public_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Portal link copied");
    } catch {
      window.prompt("Copy the portal link:", url);
    }
    setOpen(false);
  };

  const regenerate = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Regenerate the portal link?\n\nThe old URL will stop working immediately. Anyone who had the old link (including forwarded email recipients) will need a fresh one.")) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${ticket.id}/portal-link/regenerate`);
      toast.success("New portal link issued");
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally {
      setBusy(false); setOpen(false);
    }
  };

  const revoke = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Revoke the portal link?\n\nThe URL will stop working immediately. The requester will need to be issued a fresh link before they can view the ticket again.")) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${ticket.id}/portal-link/revoke`);
      toast.success("Portal link revoked");
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally {
      setBusy(false); setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#4f4c90] transition"
        data-testid="ticket-portal-link-menu"
        title={active ? "Manage the requester-facing portal link" : "No active portal link — regenerate to issue one"}
      >
        <LinkIcon size={11} weight="duotone" />
        {active ? (isExpired ? "Link expired" : "Portal link") : "No link"}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 top-5 z-50 w-56 rounded-md border border-slate-200 bg-white shadow-lg p-1 text-xs"
            data-testid="portal-link-popover"
          >
            {active && !isExpired && (
              <button
                type="button"
                onClick={copy}
                className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100"
                data-testid="portal-link-copy"
              >
                Copy link
              </button>
            )}
            <button
              type="button"
              onClick={regenerate}
              disabled={busy}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 disabled:opacity-60"
              data-testid="portal-link-regenerate"
            >
              {active ? "Regenerate (invalidates old link)" : "Issue new link"}
            </button>
            {active && (
              <button
                type="button"
                onClick={revoke}
                disabled={busy}
                className="w-full text-left px-2 py-1.5 rounded hover:bg-red-50 text-red-600 disabled:opacity-60"
                data-testid="portal-link-revoke"
              >
                Revoke link
              </button>
            )}
            <div className="border-t border-slate-100 mt-1 pt-1.5 px-2 pb-1 text-[10px] text-slate-500 font-mono leading-snug">
              {active
                ? (expiresAt
                    ? (isExpired
                        ? "Expired — regenerate to reactivate."
                        : `Expires ${expiresAt.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} (${daysLeft}d)`)
                    : "No expiry set (legacy link).")
                : "No active link. Regenerate to issue one."}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


/**
 * §12 — Teams (queue) membership control. Small sidebar card that shows
 * which queues the ticket belongs to and lets an agent move it between
 * IT and Bsys (or dual-post it). Backend refuses to remove the last
 * team, so orphaning is impossible. Optimistic UI: bubble the new
 * teams list up so the parent's ticket object stays in sync without
 * a full refetch.
 */
function TeamsCard({ ticket, onChange }) {
  const [busy, setBusy] = useState(false);
  const teams = ticket.teams || ["systems"];
  const hasIt = teams.includes("it");
  const hasSys = teams.includes("systems");

  const act = async (team, action) => {
    setBusy(true);
    try {
      const r = await api.post(`/tickets/${ticket.id}/teams`, { team, action });
      onChange(r.data.teams);
      toast.success(action === "add" ? `Added to ${team === "it" ? "IT" : "Bsys"}` : `Removed from ${team === "it" ? "IT" : "Bsys"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update queue");
    } finally {
      setBusy(false);
    }
  };

  // Move = add target + remove source (two calls; the ticket briefly
  // shows in both queues between requests, which is acceptable for a
  // ~200ms transition and simpler than a dedicated backend action).
  const move = async (to) => {
    setBusy(true);
    try {
      const from = to === "it" ? "systems" : "it";
      await api.post(`/tickets/${ticket.id}/teams`, { team: to, action: "add" });
      const r = await api.post(`/tickets/${ticket.id}/teams`, { team: from, action: "remove" });
      onChange(r.data.teams);
      toast.success(`Moved to ${to === "it" ? "IT" : "Bsys"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="teams-card">
      <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Queues</Label>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        {hasSys && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800"
            data-testid="teams-chip-systems"
          >
            Bsys
            {teams.length > 1 && (
              <button
                onClick={() => act("systems", "remove")}
                disabled={busy}
                title="Remove from Bsys queue"
                className="hover:text-emerald-950 disabled:opacity-40"
                data-testid="teams-remove-systems"
              >
                <XIcon size={10} />
              </button>
            )}
          </span>
        )}
        {hasIt && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-800"
            data-testid="teams-chip-it"
          >
            IT
            {teams.length > 1 && (
              <button
                onClick={() => act("it", "remove")}
                disabled={busy}
                title="Remove from IT queue"
                className="hover:text-sky-950 disabled:opacity-40"
                data-testid="teams-remove-it"
              >
                <XIcon size={10} />
              </button>
            )}
          </span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {hasSys && !hasIt && (
          <Button
            variant="outline" size="sm" onClick={() => move("it")} disabled={busy}
            className="h-7 text-[11px] px-2"
            data-testid="teams-move-to-it"
          >
            <ArrowsLeftRight size={12} className="mr-1" /> Move to IT
          </Button>
        )}
        {hasIt && !hasSys && (
          <Button
            variant="outline" size="sm" onClick={() => move("systems")} disabled={busy}
            className="h-7 text-[11px] px-2"
            data-testid="teams-move-to-systems"
          >
            <ArrowsLeftRight size={12} className="mr-1" /> Move to Bsys
          </Button>
        )}
        {!hasIt && (
          <Button
            variant="ghost" size="sm" onClick={() => act("it", "add")} disabled={busy}
            className="h-7 text-[11px] px-2 text-slate-600"
            data-testid="teams-add-it"
          >
            + Share to IT
          </Button>
        )}
        {!hasSys && (
          <Button
            variant="ghost" size="sm" onClick={() => act("systems", "add")} disabled={busy}
            className="h-7 text-[11px] px-2 text-slate-600"
            data-testid="teams-add-systems"
          >
            + Share to Bsys
          </Button>
        )}
      </div>
    </div>
  );
}


/**
 * MergeHistoryCard — lists every merge that pulled a duplicate ticket
 * into THIS primary. Shows an "Undo" button for entries that haven't
 * been reversed yet. Lives inline rather than in /ticket/*.jsx because
 * it's tiny and tightly coupled to the parent's `load()` refresh.
 *
 * Rendered on the primary ticket only — the duplicate side is already
 * soft-deleted and typically not opened directly. If a user DOES open
 * the duplicate (e.g. deep-link into /tickets/<old_id>), the generic
 * "Loading…"/404 path handles it; a dedicated banner can come later if
 * needed.
 */
function MergeHistoryCard({ ticket, onReload }) {
  const history = (ticket && ticket.merge_history) || [];
  const [busyId, setBusyId] = useState(null);
  if (!history.length) return null;

  const undo = async (dupId, dupNumber) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Undo the merge of #${dupNumber} into #${ticket.number}?\n\n` +
      `• Comments, attachments and activity brought in by that merge move back to #${dupNumber}.\n` +
      `• New activity on #${ticket.number} since the merge stays here.\n` +
      `• #${dupNumber} is restored.\n\n` +
      `You can undo at any time; a merge audit entry is kept on both tickets.`
    );
    if (!ok) return;
    setBusyId(dupId);
    try {
      await api.post(`/tickets/${ticket.id}/merge/${dupId}/undo`);
      toast.success(`Restored #${dupNumber}`);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="hairline rounded-lg p-3 bg-white" data-testid="merge-history-card">
      <div className="text-[11px] uppercase text-slate-500 tracking-wider mb-2 font-medium flex items-center gap-1.5">
        <ArrowsLeftRight size={12} weight="duotone" className="text-[#4f4c90]" />
        Merge history
      </div>
      <ul className="space-y-1.5">
        {history.map((h) => {
          const undone = !!h.undone_at;
          const dupId = h.merged_ticket_id;
          return (
            <li
              key={`${dupId}-${h.merged_at}`}
              className="flex items-center justify-between gap-2 text-xs"
              data-testid={`merge-history-row-${h.merged_ticket_number}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-slate-800">
                  #{h.merged_ticket_number} merged in
                  <span className="text-slate-500 font-mono"> · {timeAgo(h.merged_at)}</span>
                  {h.merged_by_name && (
                    <span className="text-slate-500"> · {h.merged_by_name}</span>
                  )}
                </div>
                {undone && (
                  <div className="text-[11px] text-slate-500 font-mono">
                    Undone {timeAgo(h.undone_at)}{h.undone_by_name ? ` · ${h.undone_by_name}` : ""}
                  </div>
                )}
              </div>
              {!undone && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => undo(dupId, h.merged_ticket_number)}
                  disabled={busyId === dupId}
                  data-testid={`merge-history-undo-${h.merged_ticket_number}`}
                  className="h-6 text-[11px] px-2 border-slate-200 bg-slate-50 hover:bg-slate-100 gap-1"
                >
                  <ArrowUUpLeft size={11} />
                  {busyId === dupId ? "Undoing…" : "Undo"}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


